import React, { useState, useEffect } from 'react';
import cx from 'classnames';
import { Flex, Input } from 'components';
import { useTranslation } from 'react-i18next';
import { ProductItem } from 'lane-shared/domains/billingPayments/types';
import {
  type SupportedPaymentCurrency,
  formatCurrencyAmount,
  getCurrencySymbol,
  getCurrencyPrecision,
} from 'domains-billing-currency';
import {
  multiplyByQuantity,
  calculateNewAmountFromRate,
  getCurrentListPrice,
  getPricing,
  getMinimumRate,
  getOriginalListPrice,
} from 'domains-billing-calculation';
import { PriceException } from '../PriceException';

import { M } from 'components/typography';

import styles from './ItemRow.scss';
import { Icon } from 'design-system-web';
import { SupportedLocaleEnum } from 'localization';
import { productToPriceComponents } from 'lane-shared/domains/billingPayments/helpers';
import { limitValue } from 'lane-shared/domains/billingPayments/limitValue';
import { useDebouncedCallback } from 'use-debounce';
import {
  useServiceRequestChargeOverrideEnabled,
  useServiceRequestChargeOverridePermissionEnabled,
  useBpAdminFeeChargeCodeEnabled,
} from 'react-hooks';
import {
  createUpdatedProductItem,
  shouldShowToast,
  getProductTypeLabel,
} from '../helpers';
import { RateChangeIndicator } from '../RateChangeIndicator';

function Total({
  quantity,
  rate,
  listPrice,
  currency,
  isChargeOverride,
  locale,
}: {
  quantity: number;
  rate: number;
  listPrice: number;
  currency: SupportedPaymentCurrency;
  isChargeOverride: boolean;
  locale: SupportedLocaleEnum;
}) {
  const baseRate = isChargeOverride ? rate : listPrice;
  const precision = getCurrencyPrecision(currency);
  const total = multiplyByQuantity(baseRate, quantity, precision).toNumber();

  return formatCurrencyAmount(currency, total, locale);
}

export function ItemRow({
  item,
  currency,
  locale,
  update,
  remove,
  isDraft = false,
  channelId,
}: {
  item?: ProductItem;
  currency: SupportedPaymentCurrency;
  locale: SupportedLocaleEnum;
  update?: (updatedProduct: ProductItem) => void;
  remove?: () => void;
  isDraft?: boolean;
  channelId?: string;
}) {
  const { t } = useTranslation();
  const isAdminFeeFeatureEnabled = useBpAdminFeeChargeCodeEnabled();
  const isEmpty = !item;
  const product = item?.product!;
  const [quantity, setQuantity] = useState(item?.quantity!);

  const { canEditChargeRate } =
    useServiceRequestChargeOverridePermissionEnabled(channelId || '');

  const isServiceRequestChargeOverrideEnabled =
    useServiceRequestChargeOverrideEnabled();

  const isChargeOverride =
    isServiceRequestChargeOverrideEnabled && canEditChargeRate && isDraft;

  const productTypeLabel = () => {
    if (isEmpty) return '-';

    return getProductTypeLabel(product.productCategory.productType, t);
  };

  let listPrice = 0;

  const currencySymbol = getCurrencySymbol(currency, locale);

  if (!isEmpty) {
    const priceComponents = productToPriceComponents(product);

    ({ listPrice } = getPricing({
      currency,
      locale,
      priceComponents,
      isAdminFeeFeatureEnabled,
    }));
  }

  const [displayPrice, setDisplayPrice] = useState<number | string>(listPrice);
  const [isUserEditing, setIsUserEditing] = useState(false);

  useEffect(() => {
    // Only update displayPrice from listPrice if user is not actively editing
    if (!isEmpty && !isUserEditing) {
      setDisplayPrice(listPrice);
    }
  }, [listPrice, isEmpty, isUserEditing]);

  const minimumRate = getMinimumRate(item!);

  const debouncedRateChangeObj = useDebouncedCallback<
    (limitedValue: number) => void
  >((limitedValue: number) => {
    if (update && item) {
      const newAmount = calculateNewAmountFromRate(
        limitedValue,
        item.product.markup,
        currency,
        item.product.adminFee
      );

      const updatedItem = createUpdatedProductItem(
        item,
        newAmount,
        currency,
        locale,
        limitedValue,
        isAdminFeeFeatureEnabled
      );

      try {
        update(updatedItem);

        if (window.Toast && shouldShowToast(limitedValue, minimumRate)) {
          window.Toast.show(t('abp.charges.rateUpdated'));
        }
      } catch (error) {
        console.error('Failed to update rate:', error);

        if (window.Toast) {
          window.Toast.show(t('abp.charges.rateUpdateError'), {
            type: 'error',
          });
        }
      }
    }
  }, 1000);

  const debouncedRateChange = debouncedRateChangeObj.callback;

  // Cleanup: Cancel pending debounced updates on unmount
  useEffect(() => {
    return () => {
      debouncedRateChangeObj.cancel();
    };
  }, []);

  const debouncedOnChange = useDebouncedCallback<
    (limitedValue: number) => void
  >((limitedValue: number) => {
    if (update) {
      update({ ...item!, quantity: limitedValue });
    }
  }, 1000).callback;

  const onUpdate = (value: string) => {
    const limitedValue = limitValue(value, 2);

    debouncedOnChange(limitedValue);
    setQuantity(limitedValue);
  };

  const hasMinimumRateError =
    !isEmpty &&
    (displayPrice === '' ||
      Number.isNaN(displayPrice) ||
      Number(displayPrice) < Number(minimumRate));

  const onRateUpdate = (value: string) => {
    // Case 1: Empty field - allow it, show validation error, and notify parent
    if (value === '') {
      setDisplayPrice('');
      debouncedRateChangeObj.cancel();

      // Notify parent that rate is invalid so submit button gets disabled
      if (update && item) {
        update({ ...item, userInputRate: NaN });
      }

      return;
    }

    // Case 2: Parse and validate input
    const limitedValue = limitValue(value, 2);

    // Case 3: Invalid input (NaN) - set display but don't trigger backend update
    if (Number.isNaN(limitedValue)) {
      setDisplayPrice(limitedValue); // Show what user typed (will show validation error)

      return;
    }

    // Case 4: Valid input - update display and trigger backend update
    setDisplayPrice(limitedValue);
    debouncedRateChange(limitedValue);
  };

  const handleRateBlur = () => {
    // Reset editing flag when user leaves the field
    setIsUserEditing(false);

    // If the field was emptied or invalid, reset to listPrice and notify parent
    if (displayPrice === '' || Number.isNaN(Number(displayPrice))) {
      setDisplayPrice(listPrice);

      // Notify parent that rate is valid again (using listPrice)
      if (update && item) {
        update({ ...item, userInputRate: listPrice });
      }
    }
  };

  // Rate change indicator logic
  const shouldShowRateChangeIndicator = () => {
    if (!item?.product.originalPrice) return false;

    // Show rate change indicator when isRateOverridden=true AND originalPrice exists
    return item.isRateOverridden;
  };

  const shouldShowPriceException = () => {
    // Show price exception when isRateOverridden=false AND originalPrice exists
    return !isEmpty && item?.product.originalPrice && !item?.isRateOverridden;
  };

  return (
    <div
      className={cx(styles.Grid, {
        [styles.DraftGrid]: isDraft,
      })}
      data-test={
        isDraft && !isEmpty ? `billing-product-item-${item?.id}` : undefined
      }
    >
      <Flex direction="column" className={styles.DetailsColumn}>
        {isEmpty ? (
          <>
            <M bold>{t('punctuation.dash')}</M>
            <M>{t('punctuation.dash')}</M>
            <M bold>{t('punctuation.dash')}</M>
          </>
        ) : (
          <Flex direction="column" gap={1}>
            <M className={styles.ProductType} bold>
              {productTypeLabel()}
            </M>
            <M className={styles.ProductCategory}>
              {product.productCategory.name}
            </M>
            <M className={styles.ProductName}>{product.name}</M>
          </Flex>
        )}
      </Flex>
      <Flex
        direction="column"
        className={
          isChargeOverride ? styles.QuantityColumnWider : styles.QuantityColumn
        }
      >
        <M className={styles.ColumnHeader} mb={2}>
          {t('abp.productsServices.table.header.quantity')}
        </M>
        {isDraft ? (
          <Input
            testId="quantity-input"
            disabled={isEmpty}
            className={styles.QuantityInput}
            input={{ style: { paddingRight: '1rem' } }}
            value={quantity}
            onChange={value => onUpdate(value)}
            showClear={false}
            error={!quantity && !isEmpty ? [''] : null}
            type="number"
          />
        ) : (
          <M>{quantity}</M>
        )}
      </Flex>
      <Flex
        className={styles.RateColumn}
        direction="column"
        align={isChargeOverride ? 'flex-start' : 'flex-end'} // ← Conditional alignment
      >
        <M className={styles.ColumnHeader} mb={2}>
          {t('abp.productsServices.table.header.rate')}
        </M>
        {isChargeOverride ? (
          <Flex
            align="flex-start"
            gap={1}
            className={styles.RateInputContainer}
          >
            {shouldShowRateChangeIndicator() && item && (
              <div className={styles.RateChangeWrapper}>
                <RateChangeIndicator
                  originalRate={getOriginalListPrice(item, currency)}
                  currentRate={getCurrentListPrice(item)}
                  currency={currency}
                  locale={locale}
                  testId="rate-change-indicator"
                />
              </div>
            )}
            {shouldShowPriceException() && item?.product.originalPrice && (
              <div className={styles.PriceExceptionWrapper}>
                <PriceException
                  rate={listPrice}
                  taxValue={item.product.tax?.value}
                  taxType={item.product.tax?.taxAmountType}
                  originalPrice={item.product.originalPrice}
                  currency={currency}
                  locale={locale}
                  className={styles.PriceExceptionIcon}
                  testId="priceException"
                />
              </div>
            )}
            <Flex align="flex-start" className={styles.RateInputWithCurrency}>
              {!isEmpty && (
                <M className={styles.CurrencySymbol}>{currencySymbol}</M>
              )}
              <Input
                testId="rate-input"
                disabled={isEmpty}
                className={styles.RateInput}
                input={{
                  style: { paddingRight: '1rem' },
                  onFocus: () => setIsUserEditing(true),
                  onBlur: handleRateBlur,
                }}
                value={isEmpty ? '' : displayPrice}
                onChange={value => onRateUpdate(value)}
                showClear={false}
                error={(() => {
                  if (isEmpty) return null;

                  if (hasMinimumRateError) {
                    return [
                      t('abp.charges.minimumRateError', {
                        minRate: formatCurrencyAmount(currency, minimumRate, locale),
                      }),
                    ];
                  }

                  return null;
                })()}
                type="number"
              />
            </Flex>
          </Flex>
        ) : (
          <M className={styles.PriceExceptionAmount}>
            {shouldShowRateChangeIndicator() && item && (
              <RateChangeIndicator
                originalRate={getOriginalListPrice(item, currency)}
                currentRate={getCurrentListPrice(item)}
                currency={currency}
                locale={locale}
                className={styles.RateChangeIcon}
                testId="rate-change-indicator"
              />
            )}
            {shouldShowPriceException() && item?.product.originalPrice && (
              <PriceException
                rate={listPrice}
                taxValue={item.product.tax?.value}
                taxType={item.product.tax?.taxAmountType}
                originalPrice={item.product.originalPrice}
                currency={currency}
                locale={locale}
                className={styles.PriceExceptionIcon}
                testId="priceException"
              />
            )}
            {isEmpty ? t('punctuation.dash') : formatCurrencyAmount(currency, listPrice, locale)}
          </M>
        )}
      </Flex>
      <Flex className={styles.TotalColumn} direction="column" align="flex-end">
        <M className={styles.ColumnHeader} mb={2}>
          {t('abp.productsServices.table.header.total')}
        </M>
        <M>
          {isEmpty ? (
            t('punctuation.dash')
          ) : (
            <Total
              quantity={quantity}
              rate={
                typeof displayPrice === 'string' || Number.isNaN(displayPrice)
                  ? 0
                  : displayPrice
              }
              listPrice={listPrice}
              currency={currency}
              isChargeOverride={isChargeOverride}
              locale={locale}
            />
          )}
        </M>
      </Flex>
      {isDraft && (
        <Flex
          className={styles.DeleteColumn}
          direction="column"
          justify="flex-start"
          align="center"
        >
          <div className={styles.DeleteIconWrapper}>
            <Icon
              testId="delete-icon"
              size="medium"
              name="trash-alt"
              disabled={isEmpty}
              className={isEmpty ? styles.DisabledDeleteIcon : styles.DeleteIcon}
              onClick={remove}
            />
          </div>
        </Flex>
      )}
    </div>
  );
}
